/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/settings.scss';

import React, {useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AccessKey, SettingsTab} from '../../util/interfaces';

import {sendGaEvent} from '../../util/analytics';

import {Location, Outlet, useLocation, useNavigate} from 'react-router';

import {AppContext} from '../misc/appContext';

import {userHasRight} from '../../util/accessUtil';

import {requestPageChangePermission} from '../../util/pageChanger';

import {BluemateTabs} from '../controls';

import {showDialog} from '@dvrd/dvr-controls';

type Tab = {
    analyticsLabel: string;
    labelKey: string;
    tab: SettingsTab;
    requireRight: 'allow' | AccessKey;
    path: string;
    userBetaKey?: string;
};
const TABS: Array<Tab> = [
    {
        analyticsLabel: 'Algemeen',
        labelKey: 'settings.tabs.common',
        tab: SettingsTab.COMMON,
        requireRight: 'allow',
        path: 'algemeen'
    },
    {
        analyticsLabel: 'Gebruikers',
        labelKey: 'settings.tabs.accountUsers',
        tab: SettingsTab.ACCOUNT_USERS,
        requireRight: AccessKey.PAGE_ACCOUNT_USERS,
        path: 'gebruikers'
    },
    {
        analyticsLabel: 'Beveiliging',
        labelKey: 'settings.tabs.password',
        tab: SettingsTab.PASSWORD,
        requireRight: 'allow',
        path: 'beveiliging'
    },
    {
        analyticsLabel: 'Koppeling Bol',
        labelKey: 'settings.tabs.connection',
        tab: SettingsTab.CONNECTION,
        requireRight: AccessKey.PAGE_SETTINGS_API,
        path: 'koppeling'
    },
    {
        analyticsLabel: 'Producten & BTW',
        labelKey: 'settings.tabs.vat',
        tab: SettingsTab.VAT,
        requireRight: AccessKey.PAGE_SETTINGS_PRODUCTS,
        path: 'producten'
    },
    {
        analyticsLabel: 'Factuurinstellingen',
        labelKey: 'settings.tabs.invoices',
        tab: SettingsTab.INVOICES,
        requireRight: AccessKey.PAGE_SETTINGS_INVOICE,
        path: 'factuur-instellingen'
    },
    {
        analyticsLabel: 'E-mails',
        labelKey: 'settings.tabs.email',
        tab: SettingsTab.EMAIL,
        requireRight: AccessKey.PAGE_SETTINGS_EMAILS,
        path: 'emails'
    },
    {
        analyticsLabel: 'Exporteren',
        labelKey: 'settings.tabs.export',
        tab: SettingsTab.EXPORT,
        requireRight: AccessKey.PAGE_SETTINGS_EXPORT,
        path: 'exporteren'
    },
    {
        analyticsLabel: 'Abonnement',
        labelKey: 'settings.tabs.subscription',
        tab: SettingsTab.SUBSCRIPTION,
        requireRight: AccessKey.PAGE_SETTINGS_SUBSCRIPTION,
        path: 'abonnement'
    }
];

function getTabFromLocation(location: Location) {
    const path = location.pathname;
    if (path.endsWith('algemeen')) return SettingsTab.COMMON;
    else if (path.endsWith('gebruikers')) return SettingsTab.ACCOUNT_USERS;
    else if (path.endsWith('beveiliging')) return SettingsTab.PASSWORD;
    else if (path.endsWith('koppeling')) return SettingsTab.CONNECTION;
    else if (path.endsWith('producten')) return SettingsTab.VAT;
    else if (path.endsWith('factuur-instellingen')) return SettingsTab.INVOICES;
    else if (path.endsWith('emails')) return SettingsTab.EMAIL;
    else if (path.endsWith('exporteren')) return SettingsTab.EXPORT;
    else if (path.endsWith('abonnement')) return SettingsTab.SUBSCRIPTION;
    return null;
}

export default function Settings() {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const location = useLocation();
    const [tab, setTab] = useState(getTabFromLocation(location) ?? SettingsTab.COMMON);
    const tabs = useMemo(() => {
        return TABS.filter((tab: Tab) => {
            if (tab.userBetaKey && !user?.isSuperAdmin() && user?.getFlex(tab.userBetaKey) !== true) return false;
            return tab.requireRight === 'allow' || userHasRight(context.accountContext.account, tab.requireRight);
        });
    }, [context.accountContext.account, context.userContext.user]);
    const navigate = useNavigate();

    function onChangeTab(nextTab: SettingsTab) {
        requestPageChangePermission(() => {
            const label = tabs.find(t => t.tab === nextTab)?.analyticsLabel;
            sendGaEvent('Click', 'Instellingen', `Tab ${label}`);
            if (nextTab === SettingsTab.SUBSCRIPTION && context.userContext.user?.isDemoAccount === true) {
                showDialog(
                    t(
                        'settings.subscriptionDemoBlocked',
                        'Abonnement instellingen zijn niet beschikbaar in het demo account.'
                    ),
                    t('settings.demoAccount', 'Demo account')
                );
                return;
            }
            const path = TABS.find(t => t.tab === nextTab)?.path;
            if (!path) return;
            setTab(nextTab);
            navigate(`/instellingen/${path}`);
        });
    }

    useEffect(() => {
        setTab(getTabFromLocation(location) ?? tab);
    }, [location.pathname]);

    return (
        <div className='settings'>
            <div className='setting-tabs-container'>
                <BluemateTabs<SettingsTab>
                    tabs={tabs.map(tab => ({key: tab.tab, label: t(tab.labelKey, tab.analyticsLabel)}))}
                    activeTab={tab}
                    onChangeTab={onChangeTab}
                />
                <div className='settings-tab-content'>
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
